import { create } from 'zustand';

import { userService } from '@/services/user-service';
import { useAuthStore } from '@/stores/auth-store';
import { applyTheme } from '@/hooks/useTheme';

import type { User, UserPreferences, UpdateUserInput, UpdateUserPreferencesInput } from '@/types/user';

interface UserProfileState {
  user: User | null;
  preferences: UserPreferences | null;
  isLoading: boolean;
  error: string | null;

  loadProfile: () => Promise<void>;
  updateProfile: (data: UpdateUserInput) => Promise<void>;
  updatePreferences: (data: UpdateUserPreferencesInput) => Promise<void>;
  refreshSubscription: () => Promise<void>;

  getTheme: () => 'light' | 'dark' | 'system';
  getDefaultState: () => string;
  isPro: () => boolean;
}

export const useUserProfileStore = create<UserProfileState>((set, get) => ({
  user: null,
  preferences: null,
  isLoading: false,
  error: null,

  loadProfile: async () => {
    set({ isLoading: true, error: null });
    try {
      const user = await userService.getCurrentUser();
      const preferences = await userService.getPreferences();
      set({ user, preferences, isLoading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to load profile',
        isLoading: false,
      });
    }
  },

  updateProfile: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const user = await userService.updateProfile(data);
      set({ user, isLoading: false });
      useAuthStore.setState({ user });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to update profile',
        isLoading: false,
      });
      throw err;
    }
  },

  updatePreferences: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const preferences = await userService.updatePreferences(data);
      set({ preferences, isLoading: false });
      useAuthStore.setState({ preferences });
      if (data.theme) applyTheme(data.theme);
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to update preferences',
        isLoading: false,
      });
      throw err;
    }
  },

  refreshSubscription: async () => {
    const user = await userService.refreshTierFromRemote();
    set({ user });
  },

  getTheme: () => get().preferences?.theme ?? 'system',
  getDefaultState: () => get().user?.defaultState ?? '',

  isPro: () => {
    const { user } = get();
    if (!user) return false;
    const { tier, subscriptionStatus, betaExpiresAt } = user;

    // Beta branch: short-circuits the subscriptionStatus gate entirely.
    // Beta users have subscriptionStatus = null (no Stripe subscription).
    // M58 / ADR-036 Decision 9.
    if (tier === 'beta') {
      return betaExpiresAt != null && new Date(betaExpiresAt).getTime() > Date.now();
    }

    // Existing pro/team check (unchanged) — gates on subscriptionStatus.
    if (tier !== 'pro' && tier !== 'team') return false;
    return (
      subscriptionStatus === 'active' ||
      subscriptionStatus === 'past_due' ||
      subscriptionStatus === 'trialing'
    );
  },
}));
